import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// "When does it apply" for every threshold form: the month it starts, optionally the month it ends (inclusive), or
// "Only this month". Without this a save always started on the day it was made, so results for earlier months silently
// kept the old (or no) threshold. The start defaults to the month being worked on (effectiveWindow.js -> defaultEffectiveMonth).
//
// Reads/writes form.effective_month ("2026-10"), form.effective_until ("2026-12" or ""), form.only_this_month; the form is
// sent to the server as-is. With no end it applies from the start month onward; with an end, the months before and after
// keep whatever applied there.
const EffectiveFromField = ({ form, setForm, disabled = false }) => {
  const from = dayjs(`${form.effective_month}-01`);
  const until = form.effective_until ? dayjs(`${form.effective_until}-01`) : null;
  const untilBeforeFrom = !!until && until.isBefore(from);
  const now = dayjs().startOf('month');
  const fromLabel = from.format('MMMM YYYY');
  const untilLabel = until ? until.format('MMMM YYYY') : '';
  const ranged = !form.only_this_month && !!until && !untilBeforeFrom;
  const touchesPast = from.isBefore(now);
  const isFuture = from.isAfter(now);

  return (
    <Box>
      <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            views={['year', 'month']}
            label="Applies from"
            value={from}
            disabled={disabled}
            onChange={(value) => value && value.isValid() && setForm({ ...form, effective_month: value.format('YYYY-MM') })}
            slotProps={{ textField: { size: 'small', sx: { width: 190 } } }}
          />
          {!form.only_this_month && (
            <Box display="flex" alignItems="center" gap={1}>
              <DatePicker
                views={['year', 'month']}
                label="Applies until (optional)"
                value={until}
                disabled={disabled}
                minDate={from}
                onChange={(value) => setForm({ ...form, effective_until: value && value.isValid() ? value.format('YYYY-MM') : '' })}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 230 },
                    error: untilBeforeFrom,
                  },
                }}
              />
              {until && (
                <Button size="small" variant="text" disabled={disabled} onClick={() => setForm({ ...form, effective_until: '' })}>
                  No end
                </Button>
              )}
            </Box>
          )}
        </LocalizationProvider>
        <FormControlLabel
          control={<Checkbox checked={!!form.only_this_month} disabled={disabled} onChange={(e) => setForm({ ...form, only_this_month: e.target.checked })} />}
          label="Only this month"
        />
      </Box>
      {untilBeforeFrom && (
        <Typography variant="caption" color="error.main" component="div" sx={{ mt: 0.5 }}>
          "Applies until" cannot be before "Applies from" - pick the last month it should apply to, or clear it.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {form.only_this_month
          ? `Only ${fromLabel} uses this value - the month after goes back to whatever applied before.`
          : ranged
            ? `${fromLabel} to ${untilLabel} use this value; after ${untilLabel} it goes back to whatever applied before. Earlier months keep what they had.`
            : `${fromLabel} and every later month use this value. Earlier months keep what they had.`}
      </Typography>
      {touchesPast && !untilBeforeFrom && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {form.only_this_month
            ? `This changes the results already showing for ${fromLabel}.`
            : ranged
              ? `This changes the results already showing for ${fromLabel} to ${untilLabel}.`
              : `This changes the results already showing for ${fromLabel} onward.`}
        </Alert>
      )}
      {isFuture && (
        <Alert severity="info" sx={{ mt: 1 }}>
          This starts in {fromLabel}; results for months before it are not affected.
        </Alert>
      )}
    </Box>
  );
};

export default EffectiveFromField;
